import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { UserRole } from '../types';

type ThemePref = 'light' | 'dark' | 'system';
type Resolved = 'light' | 'dark';

interface ThemeContextType {
  /** User's current preference (may be 'system'). */
  theme: ThemePref;
  /** Resolved actual mode after applying system preference. */
  resolved: Resolved;
  /** Set the preference. Persists to localStorage. */
  setTheme: (next: ThemePref) => void;
  /** Legacy — current resolved mode is dark. */
  isDark: boolean;
}

const STORAGE_KEY = 'kula-admin-theme';

const ThemeContext = createContext<ThemeContextType>({
  theme: 'system',
  resolved: 'light',
  setTheme: () => {},
  isDark: false,
});

function readStored(): ThemePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch { /* ignore */ }
  return 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const isStaff = user?.role === UserRole.INSTRUCTOR || user?.role === UserRole.ADMIN;

  const [theme, setThemeState] = useState<ThemePref>(() => readStored());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  // Watch system preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const resolved: Resolved = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  // Only apply dark class for staff users — students stay light-only until
  // each student page has been reviewed for dark-mode rendering.
  const shouldApplyDark = isStaff && resolved === 'dark';

  useEffect(() => {
    const html = document.documentElement;
    if (shouldApplyDark) html.classList.add('dark');
    else html.classList.remove('dark');
    return () => html.classList.remove('dark');
  }, [shouldApplyDark]);

  const setTheme = useCallback((next: ThemePref) => {
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme, isDark: shouldApplyDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
